import { useMemo } from 'react';
import { Stack, useToast, Flex, Button, Select } from '@chakra-ui/react';
import ZaraButton from 'components/custom-components/buttons/Button';
import { useMutation, useApolloClient } from '@apollo/client';
import { ChangeTimeZone } from 'graphql/mutations/ChangeTimeZone';
import { useNavigate } from 'react-router-dom';
import { useError } from 'error-provider/ErrorProvider';
import { PATHS } from 'router/routes';
import { variants } from 'utils/constants';
import CreateAgentFormWrapper from '../components/CreateAgentFormWrapper';
import { z } from 'zod';
import { useGetMeQuery, useGetTimeZonesQuery, useGetUserAgentsQuery } from 'graphql/generated';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const schema = z.object({
    timeZone: z.string().min(1),
});

type Schema = z.infer<typeof schema>;

const getCurrentTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').join(' - ')
        : '';
};

const TimeZone = () => {
    const toast = useToast();
    const { showError } = useError();
    const router = useNavigate();
    const { control, handleSubmit } = useForm<Schema>({
        defaultValues: {
            timeZone: getCurrentTimeZone(),
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const { data } = useGetTimeZonesQuery({
        onError(error) {
            showError(undefined, error);
        },
    });

    const apolloClient = useApolloClient();

    const { data: meData } = useGetMeQuery({
        onError(error) {
            showError(undefined, error);
        },
    });

    const { data: agents } = useGetUserAgentsQuery({
        onError(error) {
            showError(undefined, error);
        },
    });

    const timeZones = useMemo(() => data?.getTimeZones.listTimeZones ?? [], [data]);

    const [changeTimeZone, { loading }] = useMutation(ChangeTimeZone, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const onSubmit = (data: Schema) => {
        const userId = meData?.getMe?.__typename === 'UserModelType' ? meData.getMe.id : '';

        changeTimeZone({
            variables: {
                agentId: agents?.getUserAgents.id ?? '',
                timeZone: data.timeZone,
            },
            onCompleted(data) {
                if (data?.changeTimeZone?.__typename === 'SuccessType') {
                    apolloClient.cache.modify({
                        id: `UserModelType:${userId}`,
                        fields: {
                            isAllowToStepper() {
                                return {
                                    isAllowToStepper: true,
                                    has_subscription: true,
                                    variant: variants.Default,
                                };
                            },
                        },
                    });

                    router(PATHS.MY_AGENT);

                    toast({
                        title: 'Welcome to DialZara Dashboard',
                        isClosable: true,
                        status: 'success',
                    });
                } else {
                    toast({
                        title: data?.changeTimeZone.message,
                        isClosable: true,
                        status: 'error',
                    });
                }
            },
        });
    };

    return (
        <CreateAgentFormWrapper
            title='Set your time zone'
            description='Your AI receptionist is on call 24/7, and will use this time zone to know what time and date calls occur.'
        >
            <Stack as='form' spacing='5' onSubmit={handleSubmit(onSubmit)}>
                <Controller
                    control={control}
                    name='timeZone'
                    render={({ field: { onChange, value }, formState: { errors } }) => (
                        <Select
                            placeholder='Select time zone'
                            value={value}
                            onChange={onChange}
                            isInvalid={!!errors.timeZone?.message}
                        >
                            {timeZones.map((timeZone) => (
                                <option key={timeZone} value={timeZone}>
                                    {timeZone}
                                </option>
                            ))}
                        </Select>
                    )}
                />
                <Flex justifyContent='space-between'>
                    <Button onClick={() => router(PATHS.PHONE_PICK)} variant='outline' fontSize='sm'>
                        Back
                    </Button>
                    <ZaraButton variant='blackAlpha' isLoading={loading} type='submit'>
                        Finish
                    </ZaraButton>
                </Flex>
            </Stack>
        </CreateAgentFormWrapper>
    );
};

export default TimeZone;
